import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";
import { faqs, howItWorks, AnalyticsHeader } from "./helpers";

import type { TeamConfig } from "../types";

import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";
import BannerImage from "public/images/jaguars/2025-BundleAtTheBank-1486x148.jpg";
import BackgroundImage from "public/images/jaguars/364098717_679687604186622_5787981482286151273_n.jpg";

const LogoImage =
  "https://s3.us-east-2.amazonaws.com/ss-leagues/NFL/jacksonville-jaguars.png";

const config: TeamConfig = {
  teamSchema: "jaguars_flex",
  browserTitle: "Jaguars Bundle At The Bank",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Jacksonville Jaguars",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10228-jaguars-sec",
    mapId: "blockmap2d",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    isPerksEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: 3,
    allowedPaymentFailCount: 3,
  },

  isSaveCardAllowed: true,
  oktaClientId: "",
  oktaIssuer: "",
  refreshInventoryWhenEventClick: false,
  platform: vendorUUIDs.ticketmaster,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/jaguars/",

  // images
  images: {
    findGamesBannerImage: BannerImage.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage.src,
    logoImage: LogoImage + "?width=84&height=84",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: true,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    customAdaLabel:
      "Accessible seating available by calling the Jaguars Ticket Office at <br /> (904) 633 - 2000.",

    perks: {
      hidePerkPrice: true,
    },
  },

  colors: {
    primary: "#006778",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to checkout!",
      progressBarSuccessText:
        "Congrats! You've unlocked your 3 game package. You can add more games or checkout now.",
      checkoutZipCodeFailText: (
        <>
          Your transaction cannot be completed online. Please contact the team
          by emailing{" "}
          <Link
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="mailto:ticketing@jaguars.com"
          >
            ticketing@jaguars.com
          </Link>{" "}
          or calling{" "}
          <Link
            href="tel:9046332000"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
          >
            (904) 633-2000
          </Link>{" "}
          to verify your account.
        </>
      ),
      chooseAddOnsTabTitle: "Choose Gift",
      addOnsLabel: "Gifts",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,
    purchaseCredit: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Jacksonville Jaguars tickets are confirmed! Tickets will be in your account within the next 24 hours.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
